import { useEffect, useRef, useState } from 'react'
import { api, type FeedbackItem } from '../lib/api.ts'
import {
  feedbackCursor,
  saveFeedbackAndAdvance,
  type FeedbackReviewFilters,
} from '../lib/feedbackReviewSession.ts'
import { feedbackRoute } from '../lib/feedbackItem.ts'
import { feedbackAsyncScope } from '../lib/feedbackAsyncScope.ts'
import { Button } from '../components/Button.tsx'
import { FeedbackContext } from '../components/FeedbackContext.tsx'
import { icons } from '../components/icons.ts'
import { GuidedReview, type GuidedReviewProps } from './GuidedReview.tsx'

const DEFAULT_FILTERS: FeedbackReviewFilters = { status: 'pending' }

/** The feedback session owns navigation even when its verification cannot be rendered. */
export function FeedbackReview({
  eventId,
  filters = DEFAULT_FILTERS,
  horizon: initialHorizon,
  reviewActions,
  onEnsureAccount,
  onOpen,
  onHorizon,
  onExit,
}: {
  eventId: string
  filters?: FeedbackReviewFilters | undefined
  horizon?: string | undefined
  reviewActions?:
    | Pick<
        GuidedReviewProps,
        | 'visibleStatuses'
        | 'stagePositions'
        | 'onOpenInAccount'
        | 'onOpenCase'
        | 'onOpenResolutionCase'
      >
    | undefined
  onEnsureAccount: (accountId: string, isCurrent: () => boolean) => Promise<void>
  onOpen: (eventId: string, horizon: string) => void
  onHorizon: (horizon: string) => void
  onExit: () => void
}) {
  const [item, setItem] = useState<FeedbackItem>()
  const [horizon, setHorizon] = useState(initialHorizon)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string>()
  const [sequenceError, setSequenceError] = useState<string>()
  const [busy, setBusy] = useState(false)
  const [completed, setCompleted] = useState(false)
  const [saved, setSaved] = useState(false)
  const [retry, setRetry] = useState(0)
  // Drafts remain in this mounted session only, never in a URL or persistent browser storage.
  const drafts = useRef(new Map<string, string>())
  const [draftNote, setDraftNote] = useState('')
  const [showContext, setShowContext] = useState(true)
  const pending = useRef(false)
  const horizonRequest = useRef<ReturnType<typeof api.feedbackSequence> | undefined>(undefined)
  const asyncScope = useRef(feedbackAsyncScope(eventId))
  asyncScope.current.select(eventId)
  const isCurrent = asyncScope.current.capture()
  const horizonCallback = useRef(onHorizon)
  horizonCallback.current = onHorizon
  useEffect(() => {
    const scope = asyncScope.current
    scope.mount()
    return () => {
      scope.unmount()
    }
  }, [])

  useEffect(() => {
    let active = true
    const effectIsCurrent = asyncScope.current.capture()
    setItem(undefined)
    setLoading(true)
    setError(undefined)
    setCompleted(false)
    setSaved(false)
    setSequenceError(undefined)
    void api
      .feedbackItem(eventId)
      .then(async ({ item: received }) => {
        if (!active) return
        // Detail is account-scoped. Do not render it under the previous item's account.
        await onEnsureAccount(received.account_id, () => active && effectIsCurrent())
        if (!active || !effectIsCurrent()) return
        setItem(received)
        setDraftNote(drafts.current.get(eventId) ?? received.triage_note ?? '')
      })
      .catch((cause: unknown) => {
        if (active)
          setError(cause instanceof Error ? cause.message : 'Não foi possível abrir este feedback.')
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
    // Callback identity changes when the account is switched; that is not a new event.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, retry])

  useEffect(() => {
    if (horizon !== undefined) return
    let active = true
    const request = api.feedbackSequence(filters)
    horizonRequest.current = request
    void request
      .then((sequence) => {
        if (!active) return
        setHorizon(sequence.horizon)
        horizonCallback.current(sequence.horizon)
        setSequenceError(undefined)
      })
      .catch((cause: unknown) => {
        horizonRequest.current = undefined
        if (active)
          setSequenceError(
            cause instanceof Error ? cause.message : 'Não foi possível preparar a sequência.',
          )
      })
    return () => {
      active = false
    }
    // Filters are fixed for this session; retries should not restart it on account changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [horizon, retry])

  async function advance(): Promise<void> {
    if (item === undefined || !isCurrent()) return
    const frozen =
      horizon ?? (await (horizonRequest.current ?? api.feedbackSequence(filters))).horizon
    if (!isCurrent()) return
    if (horizon === undefined) {
      setHorizon(frozen)
      onHorizon(frozen)
    }
    const sequence = await api.feedbackSequence(filters, feedbackCursor(item), frozen)
    if (!isCurrent()) return
    setSequenceError(undefined)
    if (sequence.next === null) setCompleted(true)
    else onOpen(sequence.next.id, sequence.horizon)
  }

  async function skip(): Promise<void> {
    if (pending.current) return
    pending.current = true
    setBusy(true)
    try {
      await advance()
    } catch (cause) {
      if (isCurrent())
        setSequenceError(
          cause instanceof Error ? cause.message : 'Não foi possível avançar. Tente novamente.',
        )
    } finally {
      pending.current = false
      if (asyncScope.current.isMounted()) setBusy(false)
    }
  }

  async function save(verdict: string, note: string): Promise<void> {
    if (item === undefined || pending.current) return
    pending.current = true
    setBusy(true)
    setSequenceError(undefined)
    try {
      await saveFeedbackAndAdvance(
        () => api.triageFeedback(item.id, verdict, note),
        advance,
        () => {
          drafts.current.delete(item.id)
          if (!isCurrent()) return
          setSaved(true)
          setItem((current) =>
            current === undefined || current.id !== item.id
              ? current
              : { ...current, verdict, triage_note: note, triaged_by: null, triaged_at: null },
          )
        },
      )
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : 'Não foi possível salvar ou avançar.'
      if (isCurrent()) setSequenceError(message)
      throw cause
    } finally {
      pending.current = false
      if (asyncScope.current.isMounted()) setBusy(false)
    }
  }

  async function clear(): Promise<void> {
    if (item === undefined || pending.current) return
    pending.current = true
    setBusy(true)
    try {
      await api.clearFeedbackTriage(item.id)
      if (isCurrent()) setRetry((value) => value + 1)
    } finally {
      pending.current = false
      if (asyncScope.current.isMounted()) setBusy(false)
    }
  }

  const focus = item === undefined ? undefined : feedbackRoute(item)
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="tertiary"
          tone="neutral"
          size="small"
          leadingIcon={icons.back}
          disabled={busy}
          onClick={onExit}
        >
          Feedbacks
        </Button>
        <h1 className="text-tH4 text-text-strong">Triagem de feedbacks</h1>
        <span className="text-tSmall text-text-weak">
          Mais recentes primeiro ·{' '}
          {filters.status === 'pending'
            ? 'Não classificados'
            : filters.status === 'all'
              ? 'Todos'
              : filters.status === 'agree'
                ? 'Concordo'
                : 'Discordo'}
        </span>
        <Button
          variant="secondary"
          tone="neutral"
          size="small"
          className="ml-auto min-[1440px]:hidden"
          onClick={() => setShowContext((shown) => !shown)}
        >
          {showContext ? 'Recolher feedback' : 'Ver feedback'}
        </Button>
      </div>
      <div className="relative flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto min-[1024px]:overflow-hidden min-[1440px]:flex-row">
        <aside
          aria-label="Contexto do feedback"
          className={`border-stroke-weak bg-background-raised rounded-base order-first shrink-0 flex-col gap-3 border p-4 min-[1024px]:absolute min-[1024px]:inset-y-0 min-[1024px]:right-0 min-[1024px]:z-20 min-[1024px]:w-[340px] min-[1024px]:overflow-y-auto min-[1024px]:shadow-raised min-[1440px]:static min-[1440px]:order-last ${showContext ? 'flex' : 'hidden min-[1440px]:flex'}`}
        >
          <h2 className="text-tH5 text-text-strong">Feedback em análise</h2>
          {saved && (
            <p role="status" className="text-tSmall text-text-strong">
              Classificação salva.
            </p>
          )}
          {sequenceError !== undefined && (
            <p role="alert" className="text-tSmall text-text-error">
              {sequenceError}
            </p>
          )}
          {completed ? (
            <div className="flex flex-col gap-3">
              <p role="status" className="text-tP text-text-strong">
                Fim da sequência com estes filtros.
              </p>
              <p className="text-tSmall text-text-weak">
                Novos feedbacks ficam disponíveis ao voltar à fila.
              </p>
              <Button variant="primary" tone="brand" size="small" onClick={onExit}>
                Voltar aos feedbacks
              </Button>
            </div>
          ) : (
            <>
              <div className={showContext ? 'block' : 'hidden min-[1440px]:block'}>
                {item === undefined ? (
                  <p className="text-tSmall text-text-weak">
                    {loading ? 'Carregando feedback…' : (error ?? 'Feedback indisponível.')}
                  </p>
                ) : (
                  <FeedbackContext
                    key={item.id}
                    item={item}
                    draftNote={draftNote}
                    busy={busy}
                    onDraftChange={(note) => {
                      drafts.current.set(item.id, note)
                      setDraftNote(note)
                    }}
                    onSave={save}
                    onClear={clear}
                  />
                )}
              </div>
              {error !== undefined && (
                <Button
                  variant="secondary"
                  tone="neutral"
                  size="small"
                  onClick={() => setRetry((value) => value + 1)}
                >
                  Tentar novamente
                </Button>
              )}
              <Button
                variant="secondary"
                tone="brand"
                size="small"
                disabled={busy || item === undefined}
                onClick={() => void skip()}
              >
                {busy
                  ? 'Salvando ou avançando…'
                  : saved
                    ? 'Avançar para o próximo'
                    : 'Próximo sem classificar'}
              </Button>
              <p className="text-tSmall text-text-weak">
                Concordo ou Discordo salva e abre o próximo feedback. Notas não salvas ficam nesta
                sessão enquanto você avança.
              </p>
            </>
          )}
        </aside>
        <div className="flex min-h-[700px] min-w-0 flex-1 flex-col min-[1024px]:min-h-0 min-[1024px]:overflow-hidden">
          {item !== undefined && focus?.name === 'verification' && (
            <GuidedReview
              key={`${item.verification_id}:${item.id}`}
              {...reviewActions}
              id={item.verification_id}
              isSuperAdmin
              feedbackReview
              focusFinding={focus.finding}
              focusNear={focus.near}
              onExit={onExit}
            />
          )}
          {item === undefined && (
            <p className="text-tSmall text-text-weak p-4">
              {loading ? 'Abrindo a conferência…' : (error ?? 'Conferência indisponível.')}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
